import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import EditEventModal from './EditEventModal';
import Button from '../common/Button';
import './EventList.css';

dayjs.extend(isBetween);

function EventList({ eventos, onRemoveClick, onEditSave }) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);

  const findConflictingEvents = (events = []) => {
    return events.filter((currentEvent, index, allEvents) => {
      const currentStart = dayjs(`${currentEvent.data}T${currentEvent.hora}`);
      const currentEnd = currentStart.add(30, 'minute');

      return allEvents.some((otherEvent, otherIndex) => {
        if (index === otherIndex) return false;
        const otherStart = dayjs(`${otherEvent.data}T${otherEvent.hora}`);
        const otherEnd = otherStart.add(30, 'minute');

        return (
          currentStart.isBetween(otherStart, otherEnd, null, '[)') ||
          currentEnd.isBetween(otherStart, otherEnd, null, '(]') ||
          otherStart.isBetween(currentStart, currentEnd, null, '[)') ||
          otherEnd.isBetween(currentStart, currentEnd, null, '[)')
        );
      });
    });
  };

  const conflictingEvents = findConflictingEvents(eventos);

  const handleEditClick = (evento) => {
    setEventToEdit(evento);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = (updatedEvento) => {
    onEditSave(updatedEvento); // Atualiza o evento no pai
    setIsEditModalOpen(false);
    setEventToEdit(null);
  };

  if (conflictingEvents.length === 0) {
    return null;
  }

  return (
    <div
      className="eventClass overflow-y-auto p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl shadow-xl max-h-[400px] space-y-6"
      data-testid="event-list"
    >
      <h2
        className="font-bold text-gray-800 mb-6"
        data-testid="conflict-heading"
      >
        ⚠️ Conflitos de Horários Detectados
      </h2>
      <ul className="space-y-4" data-testid="event-list-ul">
        {conflictingEvents.map((evento) => {
          const formattedDate = dayjs(`${evento.data}T${evento.hora}`).format(
            'DD/MM/YYYY'
          );
          const formattedTime = dayjs(`${evento.data}T${evento.hora}`).format(
            'HH:mm'
          );

          return (
            <li
              key={evento.id}
              className="p-4 bg-white border-l-8 border-red-600 rounded-lg shadow-md flex flex-col sm:flex-row justify-between items-start sm:items-center hover:shadow-lg transition-shadow"
              data-testid={`event-${evento.id}`}
            >
              <div>
                <p className="text-lg font-semibold text-gray-800">
                  <span className="text-red-600">{evento.tipo}:</span>{' '}
                  {evento.descricao}
                </p>
                <p className="text-sm text-gray-600">
                  {formattedDate} às {formattedTime}
                </p>
                <p className="text-xs text-red-500 font-semibold mt-2">
                  Conflito detectado! Verifique o horário.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-0 w-full sm:w-auto">
                <Button
                  type="editar"
                  label="Editar"
                  onClick={() => handleEditClick(evento)}
                  className="bg-yellow-500 text-white px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  data-testid={`edit-button-${evento.id}`}
                />
                <Button
                  type="remover"
                  label="Remover"
                  onClick={() => onRemoveClick(evento)}
                  className="bg-red-500 text-white px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-red-400"
                  data-testid={`remove-button-${evento.id}`}
                />
              </div>
            </li>
          );
        })}
      </ul>

      {isEditModalOpen && eventToEdit && (
        <EditEventModal
          isOpen={isEditModalOpen}
          evento={eventToEdit}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveEdit}
          data-testid="edit-event-modal"
        />
      )}
    </div>
  );
}

EventList.propTypes = {
  eventos: PropTypes.array.isRequired,
  onRemoveClick: PropTypes.func.isRequired,
  onEditSave: PropTypes.func.isRequired,
};

export default EventList;
